import { minNoiseFloorRms, noiseFloorWindowMs, voicingClarityMin, voicingFloorDb } from './constants.js'
import type { PitchFrame } from './types.js'
import { noiseFloorRms } from './voicing.js'

/**
 * Why a capture produced no usable notes.
 *
 * `voicedFrames` answers one question — did this frame count as sung — and a capture where the
 * answer was always no collapses into a single "we didn't hear anything". That sentence covers
 * three completely different problems with three completely different remedies: a microphone
 * that is not running, a voice that is running but too quiet, and a voice that is loud but too
 * breathy for the tracker to find a pitch in. A user told to "sing a little louder" when the
 * wrong input device is selected will sing louder for ever.
 *
 * The frames already distinguish all three. This reads them, and reports the numbers it read so
 * the verdict can be checked rather than believed — the failure state is the one place a
 * measurement tool most needs to show its working.
 *
 * @see docs/superpowers/specs/2026-09-06-vocal-pitch-training-design.md §5.1, §9
 */

/**
 * Peak RMS below this is not a quiet room — it is no room at all.
 *
 * 1e-3 is -60 dBFS. A live microphone in a treated, silent room still delivers its own self-noise
 * at roughly -50 to -40 dBFS, so nothing that is actually recording sits below this. A muted
 * input, a stream from a device that is not the one the user is singing into, or a permission
 * that was granted and then revoked all sit at or near digital zero. The gap between -60 and -40
 * is wide enough that no real setup lands in it by accident.
 */
export const noSignalRms = 1e-3

export type CaptureDiagnosisKind = 'no-signal' | 'too-quiet' | 'unclear' | 'ok'

export interface CaptureDiagnosis {
  kind: CaptureDiagnosisKind
  /** Loudest frame in the capture, dBFS. */
  peakDb: number
  /** The measured floor, dBFS — from the same lead-in window the voicing gate uses. */
  noiseFloorDb: number
  /** Best clarity among frames loud enough to count. 0 when none were. */
  bestClarity: number
  /** Voiced frames as a fraction of the frames after the lead-in. */
  voicedFraction: number
}

/** dBFS, floored so digital silence prints a number instead of `-Infinity`. */
const toDb = (rms: number) => 20 * Math.log10(Math.max(rms, minNoiseFloorRms))

export const diagnoseCapture = (frames: PitchFrame[]): CaptureDiagnosis => {
  const floor = noiseFloorRms(frames)
  const peak = frames.reduce((loudest, frame) => Math.max(loudest, frame.rms), 0)

  /** Loud enough to be the user, whatever the tracker made of it. */
  const loudEnough = frames.filter(frame => 20 * Math.log10(Math.max(frame.rms, minNoiseFloorRms) / floor) >= voicingFloorDb)
  const bestClarity = loudEnough.reduce((best, frame) => Math.max(best, frame.clarity), 0)

  /** The lead-in is deliberately silent; counting it would halve every reading. */
  const afterLeadIn = frames.filter(frame => frame.time >= noiseFloorWindowMs / 1000)
  const voiced = afterLeadIn.filter(
    frame =>
      frame.frequency > 0 &&
      frame.clarity >= voicingClarityMin &&
      20 * Math.log10(Math.max(frame.rms, minNoiseFloorRms) / floor) >= voicingFloorDb
  )

  const measurements = {
    peakDb: toDb(peak),
    noiseFloorDb: toDb(floor),
    bestClarity,
    voicedFraction: afterLeadIn.length === 0 ? 0 : voiced.length / afterLeadIn.length,
  }

  /**
   * Order matters, and it is the order of what the user can act on. A dead stream is also too
   * quiet and also unclear; only "check your microphone" would help, so it is asked first.
   */
  if (peak < noSignalRms) {
    return { kind: 'no-signal', ...measurements }
  }
  if (loudEnough.length === 0) {
    return { kind: 'too-quiet', ...measurements }
  }
  if (bestClarity < voicingClarityMin) {
    return { kind: 'unclear', ...measurements }
  }
  return { kind: 'ok', ...measurements }
}
